import {
  PubSubEvent,
  PubSubEventListener,
  PubSubEventName,
  PubSubEventNameWithoutData,
  PubSubSubscription,
  PubSubSubscriptionDispose,
  PubSubSubscriptionOptions,
} from './pubSub.types';

/**
 * Represents an event broker for exchanging events between various parts of the
 * application. This in turn facilitates a loosely bound communication between
 * publishers and subscribers as communication is strictly controlled through
 * the broker.
 */
class PubSub {
  /** The current subscriptions. */
  private readonly subscriptions: Partial<
    Record<PubSubEventName, Set<PubSubSubscription>>
  > = {};

  /**
   * Publishes a data-less event on the event broker.
   *
   * @param name The event name.
   */
  publish<TEventName extends PubSubEventNameWithoutData>(
    name: TEventName,
  ): void;
  /**
   * Publishes an event carrying data on the event broker.
   *
   * @param name The event name.
   * @param data The event data.
   */
  publish<TEventName extends PubSubEventName>(
    name: TEventName,
    data: PubSubEvent[TEventName],
  ): void;
  publish(name: PubSubEventName, data?: PubSubEvent[PubSubEventName]): void {
    const subscriptions = this.getSubscriptions(name);
    subscriptions.forEach((subscription) => {
      // We cast to `any` as the overload cannot infer the correct event name
      // from the generics when getting subscriptions.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (subscription as any).listener(data);
      if (subscription.options.once) {
        subscriptions.delete(subscription);
      }
    });
  }

  /**
   * Subscribes to an event on the event message broker.
   *
   * @param name The event name.
   * @param listener The event listener.
   * @param options The options.
   */
  subscribe<TEventName extends PubSubEventName>(
    name: TEventName,
    listener: PubSubEventListener<TEventName>,
    options: PubSubSubscriptionOptions = { once: false },
  ): PubSubSubscriptionDispose {
    const subscription = { listener, options };
    const subscriptions = this.getSubscriptions(name);
    subscriptions.add(subscription);

    return () => {
      if (subscriptions.has(subscription)) {
        subscriptions.delete(subscription);
      }
    };
  }

  /**
   * Gets the subscriptions for an event.
   *
   * @param name The event name.
   */
  private getSubscriptions<TEventName extends PubSubEventName>(
    name: TEventName,
  ): Set<PubSubSubscription<TEventName>> {
    if (!this.subscriptions[name]) {
      this.subscriptions[name] = new Set();
    }

    return this.subscriptions[name] as Set<PubSubSubscription<TEventName>>;
  }
}

export const pubSub = new PubSub();
